/**
 * @file usePublicationsUi.ts
 * @description Окна черновика страницы в конструкторе (phase19.16 Р3а;
 * uiux-r3 §3.1): панель «Публикации» (на ПК — группа «Публикации» в
 * «Настройках страницы» по `?group=publications`, до 1280 — шторка), «Все
 * версии», окно «Вернуть версию N», «Удалённые страницы» и «Посмотреть»
 * версию на канве (полосу «Версия 6 от 03.10 — так было на сайте» рисует
 * витрина, над канвой — [Вернуть эту версию] [Закрыть]). Сменили страницу — просмотр версии
 * закрывается (канва иначе показала бы версию прежней страницы).
 */

import { useCallback, useEffect, useState } from "react";

import type { BuilderLayout, CanvasOverride } from "../../components/site-builder/builder-context";
import type { BuilderSelection } from "../../lib/site-builder/selection";
import { formatShopDateTime } from "../../lib/shop-time";

/** Ключ группы «Публикации» в «Настройках страницы» (SB122 `?group=`). */
export const PUBLICATIONS_GROUP = "publications";

interface Deps {
  layout: BuilderLayout;
  pageId: string | null;
  canRollback: boolean;
  select: (patch: Partial<BuilderSelection>) => Promise<boolean>;
  override: CanvasOverride | null;
  setOverride: (override: CanvasOverride | null) => void;
}

export interface PublicationsUi {
  publicationsOpen: boolean;
  closePublications: () => void;
  openPublications: () => void;
  versionsOpen: boolean;
  openVersions: () => void;
  closeVersions: () => void;
  /** Версия в окне «Вернуть версию N»; `null` — окно закрыто. */
  rollbackVersion: number | null;
  openRollback: (version: number) => void;
  closeRollback: () => void;
  deletedOpen: boolean;
  openDeletedPages: () => void;
  closeDeletedPages: () => void;
  viewPageVersion: (version: { version: number; publishedAt: string; isLive: boolean }) => void;
}

export function usePublicationsUi({ layout, pageId, canRollback, select, override, setOverride }: Deps): PublicationsUi {
  const [publicationsOpen, setPublicationsOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [rollbackVersion, setRollbackVersion] = useState<number | null>(null);
  const [deletedOpen, setDeletedOpen] = useState(false);

  const openPublications = useCallback(() => {
    // На ПК панель справа — та же группа в «Настройках страницы»; до 1280 — шторка.
    if (layout === "desktop") void select({ sel: { kind: "page" }, panel: null, group: PUBLICATIONS_GROUP });
    else setPublicationsOpen(true);
  }, [layout, select]);

  const viewPageVersion = useCallback(
    (v: { version: number; publishedAt: string; isLive: boolean }) => {
      if (!pageId) return;
      setVersionsOpen(false);
      setPublicationsOpen(false);
      setOverride({
        // Ключ черновика вида сайта: шапка и подвал — черновые, тело — версия страницы.
        source: { source: "draft" },
        pageVersion: { pageId, version: v.version },
        label: `Версия ${v.version} от ${formatShopDateTime(v.publishedAt)} — так было на сайте`,
        ...(!v.isLive && canRollback ? { action: { label: "Вернуть эту версию", run: () => setRollbackVersion(v.version) } } : {}),
      });
    },
    [canRollback, pageId, setOverride],
  );

  // Другая страница — просмотр версии прежней закрывается.
  const viewingPage = override?.pageVersion?.pageId ?? null;
  useEffect(() => {
    if (viewingPage && viewingPage !== pageId) setOverride(null);
  }, [pageId, viewingPage, setOverride]);

  return {
    publicationsOpen,
    closePublications: () => setPublicationsOpen(false),
    openPublications,
    versionsOpen,
    openVersions: () => setVersionsOpen(true),
    closeVersions: () => setVersionsOpen(false),
    rollbackVersion,
    openRollback: setRollbackVersion,
    closeRollback: () => setRollbackVersion(null),
    deletedOpen,
    openDeletedPages: () => setDeletedOpen(true),
    closeDeletedPages: () => setDeletedOpen(false),
    viewPageVersion,
  };
}
